import React from "react";
import styled from "styled-components";
import { useHistoricalData } from "../hooks/useHistoricalData";
import { ViewType } from "../utils/weekUtils";
import TopNavBar from "../components/navigation/TopNavBar";
import WeekNavigator from "../components/history/WeekNavigator";
import MachineTypePillFilter from "../components/history/MachineTypePillFilter";
import DataTable from "../components/DataTable";
import PieChartSection from "../components/PieChartSection";

const PageContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px 32px;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FilterRow = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  letter-spacing: -0.5px;
`;

const ViewSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ViewPill = styled.button<{ $isActive: boolean }>`
  padding: 4px 14px;
  border-radius: 999px;
  border: 1.5px solid ${(props) => (props.$isActive ? "#1a56db" : "#d0d5dd")};
  background: ${(props) => (props.$isActive ? "#e8f0fe" : "#ffffff")};
  color: ${(props) => (props.$isActive ? "#1a56db" : "#555")};
  font-size: 13px;
  font-weight: ${(props) => (props.$isActive ? "600" : "400")};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${(props) => (props.$isActive ? "#e8f0fe" : "#f5f5f5")};
    border-color: ${(props) => (props.$isActive ? "#1a56db" : "#9e9e9e")};
  }
`;

const ChartsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const LoadingMessage = styled.p`
  text-align: center;
  color: #888;
  font-size: 14px;
  margin: 40px 0;
`;

const ErrorMessage = styled.p`
  text-align: center;
  color: #c62828;
  font-size: 14px;
  margin: 40px 0;
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: #999;
  font-size: 14px;
  margin: 40px 0;
`;

const VIEW_OPTIONS: { label: string; value: ViewType }[] = [
  { label: "Week", value: "Weekly" },
  { label: "Month", value: "Monthly" },
  { label: "Quarter", value: "Quarterly" },
  { label: "Year", value: "Yearly" },
];

const PAGE_TITLE: Record<ViewType, string> = {
  Weekly: "Weekly History",
  Monthly: "Monthly History",
  Quarterly: "Quarterly History",
  Yearly: "Yearly History",
};

// =============================================
// Displays the historical view for the selected
// time period (weekly, monthly, quarterly, yearly).
//
// Defaults to the previous week on first load.
// The WeekNavigator allows stepping back and
// forward; navigation past the current period
// is blocked.
//
// The DataTable is shown only for the weekly
// view. Pie charts are shown for all views.
// =============================================
const HistoryPage: React.FC = () => {
  const {
    dayData,
    isLoading,
    fetchError,
    periodLabel,
    isCurrentPeriod,
    viewType,
    setViewType,
    selectedMachineType,
    selectMachineType,
    goToPrevious,
    goToNext,
  } = useHistoricalData();

  return (
    <>
      <TopNavBar />
      <PageContainer>
        <ControlRow>
          <TitleGroup>
            <PageTitle>{PAGE_TITLE[viewType]}</PageTitle>
            <ViewSwitcher role="group" aria-label="Select view period">
              {VIEW_OPTIONS.map(({ label, value }) => (
                <ViewPill
                  key={value}
                  $isActive={viewType === value}
                  onClick={() => setViewType(value)}
                  aria-pressed={viewType === value}
                  type="button"
                >
                  {label}
                </ViewPill>
              ))}
            </ViewSwitcher>
          </TitleGroup>

          <WeekNavigator
            periodLabel={periodLabel}
            isCurrentPeriod={isCurrentPeriod}
            onPrevious={goToPrevious}
            onNext={goToNext}
          />
        </ControlRow>

        <FilterRow>
          <MachineTypePillFilter
            selectedMachineType={selectedMachineType}
            onSelect={selectMachineType}
          />
        </FilterRow>

        {isLoading && (
          <LoadingMessage>Loading data for {periodLabel}…</LoadingMessage>
        )}

        {!isLoading && fetchError && (
          <ErrorMessage>Failed to load data: {fetchError}</ErrorMessage>
        )}

        {!isLoading && !fetchError && dayData.length === 0 && (
          <EmptyMessage>
            No {selectedMachineType} operations recorded for {periodLabel}.
          </EmptyMessage>
        )}

        {!isLoading && !fetchError && dayData.length > 0 && (
          <>
            {viewType === "Weekly" && (
              <DataTable data={dayData} selectedMachineType={selectedMachineType} />
            )}

            <ChartsRow>
              <PieChartSection data={dayData} metric="parts" />
              <PieChartSection data={dayData} metric="minutes" />
            </ChartsRow>
          </>
        )}
      </PageContainer>
    </>
  );
};

export default HistoryPage;
